import { useEffect, useState } from "react";
import { Handshake, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { OWNER_FIRST_NAME } from "@/lib/constants";

export default function BrandStrip() {
    const [brands, setBrands] = useState(null);
    useEffect(() => {
        http.get("/public/brands")
            .then(({ data }) => setBrands(data.items || []))
            .catch(() => setBrands([]));
    }, []);

    if (brands === null) return null;

    // Empty state: show category chips so visitors still get the "I partner with brands" signal
    // without displaying any un-approved logos.
    if (brands.length === 0) {
        const categories = [
            "Texas electricity providers",
            "Major mobile carriers",
            "National internet / TV",
            "Home security brands",
            "Business payment processors",
        ];
        return (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="brand-strip-empty">
                <div className="card-surface p-6 sm:p-8">
                    <div className="flex items-center gap-2">
                        <Handshake className="w-4 h-4 text-emerald-300" />
                        <h3 className="text-white font-semibold text-base sm:text-lg">
                            National brands I partner with
                        </h3>
                    </div>
                    <p className="mt-2 text-sm text-slate-400 max-w-2xl">
                        I compare offerings from vetted national providers across these categories.
                        Ask {OWNER_FIRST_NAME} which specific brands are available in your service area.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <span
                                key={c}
                                data-testid={`brand-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs sm:text-sm text-slate-200 bg-white/[0.03]"
                            >
                                {c}
                            </span>
                        ))}
                        <a
                            href="sms:7132052971?&body=What brands can you compare in my area?"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/40 text-xs sm:text-sm text-emerald-200 hover:bg-emerald-500/10"
                            data-testid="brand-ask-cta"
                        >
                            Ask what's in your area <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="brand-strip">
            <div className="card-surface p-6 sm:p-8">
                <div className="flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-emerald-300" />
                    <h3 className="text-white font-semibold">National brands I partner with</h3>
                </div>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {brands.map((b) => (
                        <div
                            key={b.id}
                            data-testid={`brand-${b.id}`}
                            className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center gap-3"
                        >
                            {b.logo_url ? (
                                <img src={b.logo_url} alt={b.name} className="h-6 object-contain" />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center justify-center text-emerald-300 text-xs font-black shrink-0">
                                    {b.name.slice(0, 1).toUpperCase()}
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-white truncate">{b.name}</div>
                                {b.category && <div className="text-xs text-slate-400 truncate">{b.category}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
